import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import 'app/components/commons/Experience/Availability/utils';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { usePack } from 'app/hooks/data/core/admin/packs/usePack';
import { useGetHotel, useHotels } from 'app/hooks/data/hotels/useHotel';
import type { OpeningHours } from 'app/typings/core/admin/packs/openingHours/openingHours';
import type {
  MaxBookingConfiguration,
  PackResponse,
} from 'app/typings/core/admin/packs/pack';
import { dayjs, toDayjs } from 'app/utils/dayjs';
import { useSearchParams } from 'app/utils/searchParams';

export type PackInitialFormType = Partial<Omit<PackResponse, 'id'>> & {
  id?: number;
  hotelId?: number;
  isDaytime?: boolean;
  nameForHotel?: string;
  nameForUser?: string;
  openingHours?: OpeningHours;
  maxBookingConfiguration?: MaxBookingConfiguration;
  ownerId?: number;
};

export const usePackData = () => {
  const { isEditMode } = useContext(ViewEditModeContext);

  const { id: packIdParam } = useParams<{ id: string }>();
  const { getSearchParam } = useSearchParams();

  const hotelId = getSearchParam('hotelId');
  const isDaytime = getSearchParam('isDaytime');

  const packId = useMemo(
    () => (packIdParam ? parseInt(packIdParam) : undefined),
    [packIdParam]
  );

  const parsedHotelId = useMemo(() => {
    if (hotelId) {
      const parsed = parseInt(hotelId);

      return isNaN(parsed) ? undefined : parsed;
    }

    return undefined;
  }, [hotelId]);

  const isCreatePage = !packId;

  const { data: fetchedPack, isFetching } = usePack(packId);

  const { data: hotel } = useGetHotel(
    isCreatePage ? parsedHotelId : fetchedPack?.hotelId
  );
  const { data: hotels } = useHotels();

  const initialValues: PackInitialFormType = useMemo(() => {
    if (isCreatePage) {
      const parsedIsDaytime =
        isDaytime === 'true' ? true : isDaytime === 'false' ? false : undefined;

      return {
        hotelId: parsedHotelId,
        isDaytime: parsedIsDaytime,
        nameForHotel: undefined,
        nameForUser: undefined,
        ownerId: parsedIsDaytime ? hotel?.managerId : undefined,
      };
    }

    return {
      ...fetchedPack,
      nameForHotel: fetchedPack?.nameForHotel || '',
      nameForUser: fetchedPack?.nameForUser || '',
      isDaytime: fetchedPack?.isDaytime ?? false,
      maxBookingConfiguration: fetchedPack?.maxBookingConfiguration,
      ownerId: fetchedPack?.ownerId,
      disclaimers: fetchedPack?.disclaimers
        ?.map((disclaimer) => ({
          ...disclaimer,
          dateRange: [
            toDayjs(disclaimer.startDate) ?? dayjs(),
            toDayjs(disclaimer.endDate),
          ],
        }))
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    };
  }, [isCreatePage, fetchedPack, parsedHotelId, isDaytime, hotel]);

  const isDataLoaded = useMemo(() => {
    if (isCreatePage) {
      return !!hotels && !!hotel;
    }

    return Boolean(fetchedPack && hotel && hotels);
  }, [isCreatePage, fetchedPack, hotel, hotels]);

  const title = isCreatePage
    ? 'Create new package'
    : fetchedPack?.nameForHotel || 'Package Details';

  const buttonSaveText = isCreatePage
    ? 'Create new package'
    : isEditMode
    ? 'Save'
    : 'Edit';

  return {
    packId,
    isCreatePage,
    hotel,
    initialValues,
    isDataLoaded,
    buttonSaveText,
    title,
    fetchedPack,
    isFetching,
  };
};
